import { ACTIVE_JOB_STATES, LuxedoRPC, type JobStatus } from "luxedo-rpc"
import { v4 as uuid } from "uuid"

type ProgressReportCallback = (progress: number) => void

/** How often a context asks the API about jobs it cannot hear live (after a refresh, or in another tab). */
export const JOB_STATUS_POLL_MS = 10_000

/**
 * After an outcome has been delivered for a job, the registry's terminal record (kept for a two
 * minute grace period) and every periodic re-watch that re-reads it must not deliver it again.
 * Slightly over the grace period so the record is guaranteed gone when the window closes.
 */
export const SETTLED_OUTCOME_IGNORE_MS = 150_000

export abstract class ProgressContext<T> {
	public status: {
		[entryId: number]: number
	} = $state({})

	/** Entries whose job is waiting for one of the account's GPU slots */
	public queued: { [entryId: number]: boolean } = $state({})

	protected progressCallbacks: {
		[entryId: number]: {
			[callbackId: string]: ProgressReportCallback
		}
	}

	protected completeCallbacks: {
		[entryId: number]: {
			[callbackId: string]: () => void
		}
	}

	protected failCallbacks: {
		[entryId: number]: {
			[callbackId: string]: (...args: any) => void
		}
	}

	/** Told when a job ends without finishing, so a subscriber can drop its in-flight UI. */
	protected cancelCallbacks: {
		[entryId: number]: {
			[callbackId: string]: () => void
		}
	}

	private watched = new Set<number>()
	private pollTimer: ReturnType<typeof setInterval> | null = null

	/**
	 * The entity's completion marker as this page held it when it began following the job
	 * (see completionMarker). If the job's registry record vanishes, the marker decides whether
	 * the job finished (marker moved) or died without a trace (marker unchanged).
	 */
	private markerWhenFollowed: { [entryId: number]: string | null } = {}

	/** When an outcome was last delivered for an entry; see SETTLED_OUTCOME_IGNORE_MS. */
	private settledAt: { [entryId: number]: number } = {}

	constructor(endpoints: { progress: string; complete?: string; fail?: string; queued?: string }) {
		this.completeCallbacks = {}
		this.progressCallbacks = {}
		this.failCallbacks = {}
		this.cancelCallbacks = {}

		const { progress, complete, fail, queued } = endpoints

		LuxedoRPC.bindEndpoint(progress, (id, progress) => this.handleProgressReport(progress, id))
		if (complete) LuxedoRPC.bindEndpoint(complete, (id) => this.onSuccess(id))
		if (fail) LuxedoRPC.bindEndpoint(fail, (id, ...details) => this.onFail(id, ...details))
		if (queued) LuxedoRPC.bindEndpoint(queued, (id) => this.onQueued(id))
	}

	private onQueued = (id: number) => {
		this.rememberMarker(id)
		this.queued[id] = true
	}

	/** Snapshot the entity's completion marker the first time this job is seen in flight. */
	private rememberMarker(id: number) {
		if (id in this.markerWhenFollowed) return
		this.markerWhenFollowed[id] = this.completionMarker(id)
	}

	protected handleProgressReport(progress: number, id: number) {
		this.rememberMarker(id)
		delete this.queued[id]
		this.status[id] = progress
		const callbacks = Object.values(this.progressCallbacks[id] ?? {})
		callbacks.forEach((cb) => cb(progress))
	}

	/** An entry this context is currently following as in flight (queued or reporting progress). */
	private isFollowing(id: number): boolean {
		return this.status[id] !== undefined || !!this.queued[id]
	}

	/**
	 * Whether an outcome for this entry was delivered recently enough that the registry's terminal
	 * record for it is the same job reporting again. A page loaded after the job settled has no
	 * such memory, so the record is delivered: after a refresh this is how it learns a render
	 * failed (PR #1052 review).
	 */
	private recentlySettled(id: number): boolean {
		const at = this.settledAt[id]
		return at !== undefined && Date.now() - at < SETTLED_OUTCOME_IGNORE_MS
	}

	// An outcome is delivered once per job: the entry is forgotten when it settles and the moment is
	// remembered (settledAt), so the registry's terminal record (kept for a grace period, and re-read
	// by every periodic re-watch) and the "no record" answer that follows it cannot fire the callbacks again.
	private settle(id: number) {
		delete this.queued[id]
		delete this.status[id]
		delete this.markerWhenFollowed[id]
		this.settledAt[id] = Date.now()
		this.watched.delete(id)
	}

	private onFail = (id: number, ...args) => {
		this.settle(id)
		const callbacks = Object.values(this.failCallbacks[id] ?? {})
		callbacks.forEach((cb) => cb(...args))
	}

	/**
	 * The job ended without finishing (the object was deleted, or it was cancelled). Subscribers are
	 * told so they can drop their in-flight UI: clearing only this context's maps left a tile that
	 * had already been set to "processing" showing a frozen progress bar until the page reloaded.
	 */
	private onCancelled = (id: number) => {
		this.settle(id)
		const callbacks = Object.values(this.cancelCallbacks[id] ?? {})
		callbacks.forEach((cb) => cb())
	}

	private onSuccess = (id: number) => {
		this.settle(id)
		const callbacks = Object.values(this.completeCallbacks[id] ?? {})
		callbacks.forEach((cb) => cb())
	}

	public subscribe(
		id: number,
		callbacks: {
			progress: ProgressReportCallback
			fail?: (...args: any) => void
			success?: () => void
			cancel?: () => void
		},
	) {
		const { progress, fail, success, cancel } = callbacks
		const callbackId = uuid()

		if (!this.progressCallbacks[id]) this.progressCallbacks[id] = {}
		this.progressCallbacks[id][callbackId] = progress

		if (!this.failCallbacks[id]) this.failCallbacks[id] = {}
		if (fail) {
			this.failCallbacks[id][callbackId] = fail
		}

		if (!this.completeCallbacks[id]) this.completeCallbacks[id] = {}
		if (success) {
			this.completeCallbacks[id][callbackId] = success
		}

		if (!this.cancelCallbacks[id]) this.cancelCallbacks[id] = {}
		if (cancel) {
			this.cancelCallbacks[id][callbackId] = cancel
		}

		return callbackId
	}

	public setTempProgress(id: number, progress: number) {
		this.handleProgressReport(progress, id)
	}

	public unsubscribe(id: number, callbackId: string) {
		if (this.progressCallbacks[id] && this.progressCallbacks[id][callbackId])
			delete this.progressCallbacks[id][callbackId]
		if (this.completeCallbacks[id] && this.completeCallbacks[id][callbackId])
			delete this.completeCallbacks[id][callbackId]
		if (this.failCallbacks[id] && this.failCallbacks[id][callbackId])
			delete this.failCallbacks[id][callbackId]
		if (this.cancelCallbacks[id] && this.cancelCallbacks[id][callbackId])
			delete this.cancelCallbacks[id][callbackId]
	}

	// #region    =============== Status polling ===============

	/**
	 * Follow these entries through the API's job registry until each one finishes. Live gpq events
	 * only reach the session that started a job, so after a refresh (or in a second tab) this poll
	 * is how the page learns an entry is queued, how far along it is, that it finished, or that it
	 * failed. Cheap when nothing is in flight: the first answer with no record drops the entry.
	 */
	public watch(ids: Array<number>) {
		for (const id of ids) this.watched.add(id)
		if (this.watched.size) {
			this.pollSoon()
			if (!this.pollTimer) this.pollTimer = setInterval(() => void this.poll(), JOB_STATUS_POLL_MS)
		}
	}

	private pollSoonTimer: ReturnType<typeof setTimeout> | null = null

	/** Many tiles call watch() in the same moment (a page of scenes mounting, or their periodic
	 * re-checks firing together); answer them with one request rather than one each. */
	private pollSoon() {
		if (this.pollSoonTimer) return
		this.pollSoonTimer = setTimeout(() => {
			this.pollSoonTimer = null
			void this.poll()
		}, 250)
	}

	private stopPollingIfIdle() {
		if (this.watched.size || !this.pollTimer) return
		clearInterval(this.pollTimer)
		this.pollTimer = null
	}

	private async poll() {
		const ids = [...this.watched]
		if (!ids.length) return this.stopPollingIfIdle()
		let status: { [id: string]: JobStatus }
		try {
			status = await this.fetchStatuses(ids)
		} catch (err) {
			console.error("Could not fetch processing status", err)
			return
		}

		for (const id of ids) {
			const entry = status[String(id)]
			if (!entry) {
				// No record: never started, finished long enough ago that its record expired, or its
				// worker died and never wrote an outcome. For a job we were following, absence must
				// never mean success on its own: the entity decides (onLost).
				if (this.isFollowing(id)) void this.onLost(id)
				this.watched.delete(id)
				continue
			}
			switch (entry.state) {
				case "queued":
					// An active record is a NEW job for this entry: its own outcome must be delivered, so the
					// settled window of the previous job no longer applies (review council, 2026-09-24).
					delete this.settledAt[id]
					this.onQueued(id)
					break
				case "running":
					delete this.settledAt[id]
					this.handleProgressReport(entry.progress ?? this.status[id] ?? 0, id)
					break
				case "completed":
					// A terminal record for a job that already reported here is the same outcome again;
					// one nothing here has delivered (a job that settled before this page loaded, or
					// in another tab) is delivered now.
					if (!this.recentlySettled(id)) {
						this.handleProgressReport(1, id)
						this.onSuccess(id)
					}
					break
				case "failed":
					if (!this.recentlySettled(id)) this.onFail(id, null, entry.error ?? "failed")
					break
				case "cancelled":
					this.onCancelled(id)
					break
			}
			if (!ACTIVE_JOB_STATES.includes(entry.state)) this.watched.delete(id)
		}
		this.stopPollingIfIdle()
	}

	/**
	 * A job we were following has no registry record any more. Its worker may have died without
	 * writing an outcome (the running record then expires hours later), or it finished or failed
	 * and the terminal record aged out. Only the entity can tell: pull it and compare its completion
	 * marker with the one held when following began. Moved means it finished; unchanged means it
	 * did not, and that is reported as a failure that says so (PR #1052 review).
	 */
	private async onLost(id: number) {
		const before = this.markerWhenFollowed[id] ?? null
		let after: string | null = null
		try {
			await this.getCompletedData(id)
			after = this.completionMarker(id)
		} catch (err) {
			console.error(`Could not re-check ${this.describe(id)} after its job record disappeared`, err)
		}
		if (after !== null && after !== before) {
			this.handleProgressReport(1, id)
			this.onSuccess(id)
			return
		}
		this.onFail(
			id,
			null,
			`Processing for ${this.describe(id)} disappeared before reporting a result: nothing is running and it did not finish`,
		)
	}

	/** The registry lookup for this context's job kind. */
	protected abstract fetchStatuses(ids: Array<number>): Promise<{ [id: string]: JobStatus }>

	/**
	 * The entity field a finished job of this kind moves, as this page currently holds it (no
	 * network): null when the entity is not loaded. Compared before and after a re-pull in onLost.
	 */
	protected abstract completionMarker(id: number): string | null

	/** How to name the entity in a message, e.g. "scene 240016". */
	protected abstract describe(id: number): string

	// #endregion =============== Status polling ===============

	protected abstract getCompletedData(id: number): Promise<T>
}
